import React from 'react';
import { useHolidayMasterList } from './hooks/useHolidayMasterList';
import SharedFilters from '../../../components/SharedFilters';
import AppPagination from '../../../components/AppPagination';
import HolidayMasterTable from './components/HolidayMasterTable';

const STATUS_TABS = [
  { key: 'Active', label: 'Active' },
  { key: 'Inactive', label: 'Inactive' },
  { key: 'All', label: 'All' },
];

export default function HolidayMasterListScreen() {
  const { state, actions } = useHolidayMasterList();

  const handleStatusTabChange = (tabKey) => {
    actions.setStatusFilter(tabKey);
    actions.setPage(1);
  };

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Holiday Master
          </h1>
        </div>

        {/* Actions Cluster: Status Toggle Filter */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Tabs (Default: Active) */}
          <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
            {STATUS_TABS.map((tab) => {
              const isActive = state.statusFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => handleStatusTabChange(tab.key)}
                  className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                    isActive
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Shared Filters Accordion */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        showStatus={false}
        statusFilter={state.statusFilter}
        empGroupFilter={state.empGroupFilter}
        datePresetFilter={state.datePresetFilter}
        fromDate={state.fromDate}
        toDate={state.toDate}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setStatusFilter={actions.setStatusFilter}
        setEmpGroupFilter={actions.setEmpGroupFilter}
        setDatePresetFilter={actions.setDatePresetFilter}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}

        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}

        searchFieldOptions={state.searchFieldOptions}
        empGroupOptions={state.empGroupOptions}
        datePresetOptions={state.datePresetOptions}

        visibleAccordionFilters={[
          'empGroup',
          'datePreset',
          'fromDate',
          'toDate',
        ]}

        tools={state.tools}
        onToolSelect={actions.handleToolSelect}
      />

      {/* Holiday Master Table */}
      <section className="space-y-4">
        <HolidayMasterTable
          records={state.records}
          isLoading={state.isLoading}
          onArchive={actions.handleArchive}
          onDelete={actions.handleDelete}
          onResetFilters={actions.resetFilters}
        />

        {/* Table Footer with Pagination */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  );
}
